// Precondition: None
// Postcondition: Provides Lucide icon SVG strings for use in web components
// Invariants: All icons use consistent 24x24 viewBox with currentColor stroke

import {
  Search,
  X,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  Check,
  Plus,
  Minus,
  Star,
  Heart,
  Trash2,
  Edit,
  Settings,
  Menu,
  Filter,
  Download,
  Upload,
  ExternalLink,
  Link,
  Copy,
  Share,
  Info,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
  HelpCircle,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUpDown,
  TrendingUp,
  TrendingDown,
  BarChart2,
  PieChart,
  LineChart,
  Eye,
  EyeOff,
  Moon,
  Sun,
  SunMoon,
  Contrast,
  Home,
  Building2,
  User,
  Users,
  Briefcase,
  Calendar,
  Clock,
  FileText,
  Folder,
  FolderOpen,
  RefreshCw,
  RotateCcw,
  Loader2,
  MoreHorizontal,
  MoreVertical,
  Grip,
  GripVertical,
  PanelLeft,
  PanelLeftClose,
  PanelRight,
  Maximize2,
  Minimize2,
  Expand,
  Shrink,
  ListFilter,
  SlidersHorizontal,
  Bookmark,
  BookmarkPlus,
  Bell,
  BellOff,
  Lock,
  Unlock,
  LogOut,
  LogIn,
  CirclePower,
  CircleDollarSign,
  DollarSign,
  Percent,
  Hash,
  AtSign,
  Globe,
  Mail,
  Phone,
  MapPin,
  Navigation,
  Compass,
  Target,
  Crosshair,
  Zap,
  Flame,
  Sparkles,
  Award,
  Crown,
  Trophy,
  Medal,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Send,
  Inbox,
  Archive,
  Tag,
  Tags,
  Flag,
  Bookmark as BookmarkIcon,
  Pin,
  Paperclip,
  Layers,
  Layout,
  LayoutGrid,
  LayoutList,
  Table,
  Columns,
  Rows,
  Grid,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  Terminal,
  Database,
  Server,
  Cloud,
  CloudOff,
  Wifi,
  WifiOff,
  Bluetooth,
  Battery,
  Power,
  Play,
  Pause,
  Square,
  SkipBack,
  SkipForward,
  FastForward,
  Rewind,
  Volume,
  Volume1,
  Volume2,
  VolumeOff,
  Mic,
  MicOff,
  Camera,
  Image,
  Video,
  Film,
  Music,
  Headphones,
  Printer,
  Save,
  SaveAll,
  Undo,
  Redo,
  History,
  Scissors,
  Clipboard,
  ClipboardCheck,
  ClipboardCopy,
  ClipboardList,
  ClipboardX,
  CreditCard,
  Wallet,
  Receipt,
  ShoppingCart,
  ShoppingBag,
  Package,
  Gift,
  Truck,
  Ship,
  Plane,
  Car,
  Bike,
  Train,
  Bus,
  type IconNode,
} from "lucide";

type IconName = keyof typeof iconMap;

// Map of icon names to their Lucide icon data
const iconMap = {
  search: Search,
  x: X,
  "chevron-down": ChevronDown,
  "chevron-up": ChevronUp,
  "chevron-right": ChevronRight,
  "chevron-left": ChevronLeft,
  check: Check,
  plus: Plus,
  minus: Minus,
  star: Star,
  heart: Heart,
  trash: Trash2,
  edit: Edit,
  settings: Settings,
  menu: Menu,
  filter: Filter,
  download: Download,
  upload: Upload,
  "external-link": ExternalLink,
  link: Link,
  copy: Copy,
  share: Share,
  info: Info,
  "alert-circle": AlertCircle,
  "alert-triangle": AlertTriangle,
  "check-circle": CheckCircle,
  "x-circle": XCircle,
  "help-circle": HelpCircle,
  "arrow-up": ArrowUp,
  "arrow-down": ArrowDown,
  "arrow-left": ArrowLeft,
  "arrow-right": ArrowRight,
  "arrow-up-down": ArrowUpDown,
  "trending-up": TrendingUp,
  "trending-down": TrendingDown,
  "bar-chart": BarChart2,
  "pie-chart": PieChart,
  "line-chart": LineChart,
  eye: Eye,
  "eye-off": EyeOff,
  moon: Moon,
  sun: Sun,
  "sun-moon": SunMoon,
  contrast: Contrast,
  home: Home,
  building: Building2,
  user: User,
  users: Users,
  briefcase: Briefcase,
  calendar: Calendar,
  clock: Clock,
  "file-text": FileText,
  folder: Folder,
  "folder-open": FolderOpen,
  refresh: RefreshCw,
  "rotate-ccw": RotateCcw,
  loader: Loader2,
  "more-horizontal": MoreHorizontal,
  "more-vertical": MoreVertical,
  grip: Grip,
  "grip-vertical": GripVertical,
  "panel-left": PanelLeft,
  "panel-left-close": PanelLeftClose,
  "panel-right": PanelRight,
  maximize: Maximize2,
  minimize: Minimize2,
  expand: Expand,
  shrink: Shrink,
  "list-filter": ListFilter,
  sliders: SlidersHorizontal,
  bookmark: Bookmark,
  "bookmark-plus": BookmarkPlus,
  bell: Bell,
  "bell-off": BellOff,
  lock: Lock,
  unlock: Unlock,
  "log-out": LogOut,
  "log-in": LogIn,
  "circle-power": CirclePower,
  "circle-dollar": CircleDollarSign,
  dollar: DollarSign,
  percent: Percent,
  hash: Hash,
  "at-sign": AtSign,
  globe: Globe,
  mail: Mail,
  phone: Phone,
  "map-pin": MapPin,
  navigation: Navigation,
  compass: Compass,
  target: Target,
  crosshair: Crosshair,
  zap: Zap,
  flame: Flame,
  sparkles: Sparkles,
  award: Award,
  crown: Crown,
  trophy: Trophy,
  medal: Medal,
  "thumbs-up": ThumbsUp,
  "thumbs-down": ThumbsDown,
  message: MessageSquare,
  send: Send,
  inbox: Inbox,
  archive: Archive,
  tag: Tag,
  tags: Tags,
  flag: Flag,
  pin: Pin,
  paperclip: Paperclip,
  layers: Layers,
  layout: Layout,
  "layout-grid": LayoutGrid,
  "layout-list": LayoutList,
  table: Table,
  columns: Columns,
  rows: Rows,
  grid: Grid,
  list: List,
  "list-ordered": ListOrdered,
  "align-left": AlignLeft,
  "align-center": AlignCenter,
  "align-right": AlignRight,
  "align-justify": AlignJustify,
  bold: Bold,
  italic: Italic,
  underline: Underline,
  strikethrough: Strikethrough,
  code: Code,
  terminal: Terminal,
  database: Database,
  server: Server,
  cloud: Cloud,
  "cloud-off": CloudOff,
  wifi: Wifi,
  "wifi-off": WifiOff,
  bluetooth: Bluetooth,
  battery: Battery,
  power: Power,
  play: Play,
  pause: Pause,
  stop: Square,
  "skip-back": SkipBack,
  "skip-forward": SkipForward,
  "fast-forward": FastForward,
  rewind: Rewind,
  volume: Volume,
  "volume-1": Volume1,
  "volume-2": Volume2,
  "volume-off": VolumeOff,
  mic: Mic,
  "mic-off": MicOff,
  camera: Camera,
  image: Image,
  video: Video,
  film: Film,
  music: Music,
  headphones: Headphones,
  printer: Printer,
  save: Save,
  "save-all": SaveAll,
  undo: Undo,
  redo: Redo,
  history: History,
  scissors: Scissors,
  clipboard: Clipboard,
  "clipboard-check": ClipboardCheck,
  "clipboard-copy": ClipboardCopy,
  "clipboard-list": ClipboardList,
  "clipboard-x": ClipboardX,
  "credit-card": CreditCard,
  wallet: Wallet,
  receipt: Receipt,
  "shopping-cart": ShoppingCart,
  "shopping-bag": ShoppingBag,
  package: Package,
  gift: Gift,
  truck: Truck,
  ship: Ship,
  plane: Plane,
  car: Car,
  bike: Bike,
  train: Train,
  bus: Bus,
} as const;

// Precondition: iconData is a valid Lucide IconNode array
// Postcondition: Returns SVG string with specified size
function iconNodeToSvg(iconData: IconNode, size: number = 24): string {
  const elements = iconData
    .map(([tag, attrs]) => {
      const attrString = Object.entries(attrs)
        .map(([key, value]) => `${key}="${value}"`)
        .join(" ");
      return `<${tag} ${attrString}></${tag}>`;
    })
    .join("");

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${elements}</svg>`;
}

// Precondition: name is a valid icon name from iconMap
// Postcondition: Returns SVG string for the icon, or empty string if not found
export function icon(name: IconName, size: number = 24): string {
  const iconData = iconMap[name];
  if (!iconData) {
    console.warn(`Icon "${name}" not found`);
    return "";
  }
  return iconNodeToSvg(iconData, size);
}

// Export icon names for type checking
export type { IconName };

// Export all available icon names
export const iconNames = Object.keys(iconMap) as IconName[];
